/**
 * VIB-719: Konsolidert drift-side med 3 faner.
 *
 * Erstatter både dagens cards-baserte DriftSettings og den separate
 * /admin/observability-ruten. Faner i rekkefølge:
 *   1. Systemhelse — cron-heartbeats, Sentry, UptimeRobot (admin)
 *   2. Importdekning — PO/Monday sync-status (superadmin)
 *   3. Hendelseslogg — audit-log (admin)
 *   4. Funksjonsbrytere — feature flag-register + driftskonfig (superadmin,
 *      VIB-1396). Se `src/lib/feature-flags.ts` for registeret.
 *   5. CRM — oppfølgingsregler (superadmin, bak `feature_crm_ko_pipe`) og agentkostnader
 *      (admin, bak `feature_crm_signaler`, CRM-3.6)
 *
 * Default-fane: Systemhelse (bruker-driftens første sjekk: "fyrer det?").
 */
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SrIcon } from "@/components/ui/SrIcon";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { EventLogSection } from "@/components/settings/EventLogSection";
import { ImportCoverageSection } from "@/components/settings/ImportCoverageSection";
import { FeatureFlagsPanel } from "@/components/settings/FeatureFlagsPanel";
import { OperationalConfigSection } from "@/components/settings/OperationalConfigSection";
import { RecurringAutosendPanel } from "@/components/settings/RecurringAutosendPanel";
import { CronHeartbeatsCard } from "@/components/admin/observability/CronHeartbeatsCard";
import { SentryInfoCard } from "@/components/admin/observability/SentryInfoCard";
import { UptimeRobotInfoCard } from "@/components/admin/observability/UptimeRobotInfoCard";
import { SalesDisplayAdmin } from "@/components/display/SalesDisplayAdmin";
import { CrmCostPanel } from "@/components/settings/crm/CrmCostPanel";
import { ChatSummaryCostCard } from "@/components/settings/chat/ChatSummaryCostCard";
import { CrmRulesPanel } from "@/components/settings/crm/CrmRulesPanel";
import { CrmStyleGuidePanel } from "@/components/settings/crm/CrmStyleGuidePanel";
import { useFeatureFlag } from "@/hooks/use-feature-flag";

export default function DriftSettings() {
  const { activeUser } = useImpersonation();
  const isAdmin =
    activeUser?.role === "admin" || activeUser?.role === "superadmin";
  const isSuperadmin = activeUser?.role === "superadmin";
  const crmSignaler = useFeatureFlag("feature_crm_signaler");
  const crmEnabled = useFeatureFlag("feature_crm_ko_pipe");
  const chatRoom = useFeatureFlag("feature_chat_rom");
  const showCrmTab = (isSuperadmin && crmEnabled) || crmSignaler;

  if (!isAdmin) {
    return (
      <div className="page-container">
        <p className="text-sm text-muted-foreground">Du har ikke tilgang.</p>
      </div>
    );
  }

  return (
    <div className="page-container animate-fade-in space-y-6">
      <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2 mb-6">
        <SrIcon name="wrench" className="h-6 w-6" />
        Drift
      </h1>

      <Tabs defaultValue="systemhelse">
        <TabsList>
          <TabsTrigger value="systemhelse">Systemhelse</TabsTrigger>
          {isSuperadmin && (
            <TabsTrigger value="importdekning">Importdekning</TabsTrigger>
          )}
          <TabsTrigger value="hendelseslogg">Hendelseslogg</TabsTrigger>
          {isSuperadmin && (
            <TabsTrigger value="funksjonsbrytere">Funksjonsbrytere</TabsTrigger>
          )}
          {isSuperadmin && (
            <TabsTrigger value="salgsdisplay">Salgsdisplay</TabsTrigger>
          )}
          {showCrmTab && <TabsTrigger value="crm">CRM</TabsTrigger>}
        </TabsList>

        <TabsContent value="systemhelse" className="space-y-4 mt-4">
          <CronHeartbeatsCard />
          <div className="grid gap-4 md:grid-cols-2">
            <SentryInfoCard />
            <UptimeRobotInfoCard />
          </div>
          {chatRoom && <ChatSummaryCostCard />}
        </TabsContent>

        {isSuperadmin && (
          <TabsContent value="importdekning" className="mt-4">
            <ImportCoverageSection />
          </TabsContent>
        )}

        <TabsContent value="hendelseslogg" className="mt-4">
          <EventLogSection />
        </TabsContent>

        {isSuperadmin && (
          <TabsContent value="funksjonsbrytere" className="space-y-4 mt-4">
            <FeatureFlagsPanel />
            <RecurringAutosendPanel />
            <OperationalConfigSection />
          </TabsContent>
        )}

        {isSuperadmin && (
          <TabsContent value="salgsdisplay" className="mt-4">
            <SalesDisplayAdmin />
          </TabsContent>
        )}

        {showCrmTab && (
          <TabsContent value="crm" className="space-y-4 mt-4">
            {isSuperadmin && crmEnabled && (
              <>
                <CrmRulesPanel />
                <CrmStyleGuidePanel />
              </>
            )}
            {crmSignaler && <CrmCostPanel />}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
